import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Rocket, X } from 'lucide-react';
import { useOrganizationSettings } from '@/hooks/use-settings';

interface DemoStep {
  step: number;
  title: string;
  description: string;
  module: string;
  route: string;
}

export function DemoChecklistBanner() {
  const { data: settings } = useOrganizationSettings();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem('demo_checklist_dismissed') === 'true';
    } catch {
      return false;
    }
  });
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('demo_checklist_completed');
      return saved ? new Set(JSON.parse(saved) as number[]) : new Set<number>();
    } catch {
      return new Set<number>();
    }
  });

  const enabledModules = settings?.enabled_modules as Record<string, unknown> | undefined;
  const checklist = enabledModules?._demo_checklist as DemoStep[] | undefined;

  if (!checklist || checklist.length === 0 || dismissed) {
    return null;
  }

  const toggleStep = (step: number) => {
    setCompletedSteps(prev => {
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        next.add(step);
      }
      localStorage.setItem('demo_checklist_completed', JSON.stringify([...next]));
      return next;
    });
  };

  const validStepNumbers = new Set(checklist.map(s => s.step));
  const completedCount = [...completedSteps].filter(s => validStepNumbers.has(s)).length;
  const totalSteps = checklist.length;
  const progress = Math.round((completedCount / totalSteps) * 100);

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg mx-4 sm:mx-6 lg:mx-8 mt-4 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
        >
          <Rocket className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-800">
              Founder Demo Walkthrough
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {completedCount}/{totalSteps} steps · {progress}% complete
            </p>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            try { localStorage.setItem('demo_checklist_dismissed', 'true'); } catch { /* noop */ }
          }}
          className="ml-2 p-1 rounded hover:bg-blue-100 transition-colors cursor-pointer"
          aria-label="Dismiss demo checklist"
        >
          <X className="h-4 w-4 text-slate-400" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2">
        <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-2">
          {checklist.map((item) => {
            const done = completedSteps.has(item.step);
            return (
              <div
                key={item.step}
                className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                  done ? 'bg-green-50/60' : 'bg-white'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleStep(item.step)}
                  className="mt-0.5 flex-shrink-0 cursor-pointer"
                  aria-label={done ? `Mark step ${item.step} incomplete` : `Mark step ${item.step} complete`}
                >
                  {done ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <Circle className="h-5 w-5 text-slate-300" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${done ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                      {item.step}. {item.title}
                    </span>
                  </div>
                  <p className={`text-xs mt-0.5 ${done ? 'text-slate-400' : 'text-slate-500'}`}>
                    {item.description}
                  </p>
                  {!done && (
                    <Link
                      to={item.route}
                      className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 mt-1 font-medium"
                    >
                      Go to {item.module} →
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
